import { useState } from "react";
import { Plus, Pencil, Trash2, Lock, Users } from "lucide-react";
import { toast } from "sonner";
import { useProfiles } from "@/context/ProfileContext";
import { ProfileAvatar, ProfileEditor } from "@/components/family/ProfileEditor";
import { ft } from "@/lib/familyText";
import { useNavigate } from "react-router-dom";

export function ProfileManager({ compact = false }) {
  const T = ft();
  const navigate = useNavigate();
  const { profiles, max, plan, createProfile, updateProfile, deleteProfile } = useProfiles();
  const [editing, setEditing] = useState(null); // profile obj or {} for new
  const canAdd = profiles.length < max;
  const slotsLeft = Math.max(0, max - profiles.length);
  const isCocon = plan === "cocon";

  const handleSave = async (data) => {
    if (editing?.id) await updateProfile(editing.id, data);
    else await createProfile(data);
  };

  const handleDelete = async (p) => {
    if (!window.confirm(T.deleteConfirm)) return;
    try {
      await deleteProfile(p.id);
      toast.success("✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div data-testid="profile-manager">
      {!compact && (
        <div className="flex items-center gap-3 mb-5">
          <Users size={22} className="text-primary" />
          <div>
            <h3 className="font-display text-xl text-gradient-orange">{T.familyTitle}</h3>
            <p className="text-sm text-foreground/50">{T.familyDesc}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {profiles.map((p) => (
          <div
            key={p.id}
            data-testid={`profile-manage-card-${p.id}`}
            className="relative flex flex-col items-center gap-2.5 p-4 rounded-2xl bg-white/[0.03] border border-white/10"
          >
            <ProfileAvatar profile={p} size={72} />
            <span className="text-sm font-medium text-foreground/85 truncate max-w-full">{p.name}</span>
            <div className="flex gap-1.5">
              <button
                data-testid={`profile-edit-${p.id}`}
                onClick={() => setEditing(p)}
                className="w-8 h-8 rounded-full flex items-center justify-center bg-white/5 text-foreground/60 hover:text-primary hover:bg-primary/10 transition-colors"
                title={T.edit}
              >
                <Pencil size={14} />
              </button>
              {profiles.length > 1 && (
                <button
                  data-testid={`profile-delete-${p.id}`}
                  onClick={() => handleDelete(p)}
                  className="w-8 h-8 rounded-full flex items-center justify-center bg-white/5 text-foreground/60 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                  title={T.delete}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
        ))}

        {canAdd && (
          <button
            data-testid="profile-add-btn"
            onClick={() => setEditing({})}
            className="flex flex-col items-center justify-center gap-2.5 p-4 rounded-2xl bg-white/[0.02] border border-dashed border-white/15 hover:border-primary/50 hover:bg-primary/5 transition-colors min-h-[150px]"
          >
            <div className="w-[72px] h-[72px] rounded-2xl border border-white/15 flex items-center justify-center text-primary">
              <Plus size={28} />
            </div>
            <span className="text-sm text-foreground/60">{T.addExplorer}</span>
          </button>
        )}
      </div>

      {canAdd && (
        <p className="mt-3 text-xs text-foreground/40" data-testid="profile-slots-left">{T.slotsLeft(slotsLeft)}</p>
      )}

      {!isCocon && (
        <div
          data-testid="profile-upsell"
          className="mt-6 rounded-2xl border border-amber-400/25 bg-gradient-to-br from-amber-400/10 to-transparent p-5 flex items-start gap-4"
        >
          <div className="w-11 h-11 rounded-full bg-amber-400/15 flex items-center justify-center flex-shrink-0">
            <Lock size={20} className="text-amber-300" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-amber-200">{T.lockedTitle}</h4>
            <p className="text-sm text-foreground/60 mt-1 mb-3">{T.lockedDesc}</p>
            <button
              data-testid="profile-upsell-cta"
              onClick={() => navigate("/tarifs")}
              className="btn-frost-orange rounded-full px-5 py-2 text-sm font-semibold"
            >
              {T.lockedCta}
            </button>
          </div>
        </div>
      )}

      {editing && (
        <ProfileEditor
          initial={editing.id ? editing : null}
          onSave={handleSave}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
